/// <amd-module name="VSSUI/Components/Wizard/Wizard" />
import * as React from "react";
import { IWizardProps, IWizardItem, WizardItemState } from "./Wizard.Props";
import { Observer } from "VSSUI/Observer";
import { Icon, IIconProps, IconSize } from "VSSUI/Icon";
import { css, KeyCode } from "VSSUI/Util";
import { FocusZone, FocusZoneDirection, FocusZoneContext } from "VSSUI/FocusZone";

/**
 * A horizontal stepper for multi-step processes
 * Renders as a TabBar mostly with different styling
 */
export class Wizard extends React.Component<IWizardProps> {
    public render() {
        const { selectedIndex, items, className } = this.props;

        return (
            <Observer selectedIndex={selectedIndex} items={items}>
                {(props: { items: IWizardItem[]; selectedIndex: number }) => {
                    return (
                        <FocusZone direction={FocusZoneDirection.Horizontal}>
                            <div className={css(className, "bolt-wizard flex-row")} role="tablist">
                                {props.items.map((content, index) => this.renderItem(content, index, props.selectedIndex))}
                            </div>
                        </FocusZone>
                    );
                }}
            </Observer>
        );
    }

    private renderItem = (wizardItem: IWizardItem, index: number, selectedIndex: number) => {
        const { onItemClick } = this.props;
        const { itemState, title } = wizardItem;
        const isSelected = selectedIndex === index;

        return (
            <FocusZoneContext.Consumer key={index}>
                {zoneContext => (
                    <div
                        aria-selected={isSelected}
                        className={css("bolt-wizard-item flex-column", isSelected && "selected", onItemClick && "clickable")}
                        data-focuszone={zoneContext.focuszoneId}
                        onClick={() => onItemClick && onItemClick(index)}
                        onKeyDown={event => this.onItemKeyDown(event, index)}
                        role={"tab"}
                        tabIndex={isSelected ? 0 : -1}
                    >
                        <Observer itemState={itemState}>
                            {(props: { itemState: WizardItemState }) => {
                                const stateData = WizardItemStateInternal[props.itemState];
                                return (
                                    <div className={css("bolt-wizard-item-text-row flex-row", stateData.className)}>
                                        {stateData.iconProps && (
                                            <Icon {...stateData.iconProps} className="bolt-wizard-item-icon" size={IconSize.medium} />
                                        )}
                                        <div className="bolt-wizard-item-text body-m">{title}</div>
                                    </div>
                                );
                            }}
                        </Observer>
                        <div className="bolt-wizard-item-bar" />
                    </div>
                )}
            </FocusZoneContext.Consumer>
        );
    };

    private onItemKeyDown(event: React.KeyboardEvent<HTMLDivElement>, index: number) {
        if (event.which === KeyCode.enter) {
            this.props.onItemClick && this.props.onItemClick(index);
        }
    }
}

interface IStateData {
    className?: string;
    iconProps?: IIconProps;
}

/**
 * Internal backing struct maps WizardItemState to the necessary data
 */
const WizardItemStateInternal: { [stateName: string]: IStateData } = {
    None: {},
    Complete: {
        className: "complete",
        iconProps: { iconName: "CheckMark" }
    }
};
